import type {
  EnrichedQueuedUpload,
  EditorDocument,
  QueuedUpload,
  UploadReceipt,
} from "@/payroll/editor/types";

export const attachmentCount = (
  documents: EditorDocument[],
  uploads: QueuedUpload[],
) => {
  const savedIds = new Set(documents.map((document) => document.id));
  const uploadIds = new Set(
    documents.map((document) => document.client_upload_id),
  );
  return (
    documents.length +
    uploads.filter(
      (upload) =>
        !uploadIds.has(upload.clientId) &&
        !(upload.documentId && savedIds.has(upload.documentId)),
    ).length
  );
};

export const restoreUploads = (key: string): QueuedUpload[] => {
  try {
    const stored: unknown = JSON.parse(sessionStorage.getItem(key) || "[]");
    // Accept the former single receipt so an interrupted upload keeps its ID.
    const receipts = Array.isArray(stored) ? stored : [stored];
    return receipts
      .filter(
        (item): item is UploadReceipt =>
          typeof item?.fingerprint === "string" &&
          typeof item?.clientId === "string",
      )
      .map((receipt) => ({
        ...receipt,
        name: receipt.name ?? receipt.fingerprint.split(":")[0],
        status: "failed",
        uncertain: true,
        error:
          "Upload interrupted. Select the same file to retry, or remove this upload and check the saved files.",
      }));
  } catch {
    return [];
  }
};

export const saveUploadReceipts = (key: string, items: QueuedUpload[]) => {
  try {
    sessionStorage.setItem(
      key,
      JSON.stringify(
        items.map(({ fingerprint, clientId, name, documentId }) => ({
          fingerprint,
          clientId,
          name,
          documentId,
        })),
      ),
    );
  } catch {
    // The in-memory queue still works if browser storage is unavailable.
  }
};

export const enrichUploads = (items: QueuedUpload[]): EnrichedQueuedUpload[] =>
  items.map((item) => ({
    ...item,
    canRetry: item.status === "failed" && !!item.file && !item.removed,
  }));

export const restoreDraftUploadIds = (
  key: string,
  uploads: QueuedUpload[],
): string[] => {
  try {
    const stored: unknown = JSON.parse(
      sessionStorage.getItem(`${key}:draft`) || "null",
    );
    if (Array.isArray(stored))
      return stored.filter((id): id is string => typeof id === "string");
  } catch {
    // Interrupted receipts still restore when the draft key is unavailable.
  }
  return uploads.map((upload) => upload.clientId);
};

export const saveDraftUploadIds = (key: string, ids: string[]) => {
  try {
    sessionStorage.setItem(`${key}:draft`, JSON.stringify(ids));
  } catch {
    // Keep the in-memory draft when browser storage is unavailable.
  }
};
